import { capitalize as capitalizeFirstLetter } from 'lodash';
import type { DefineComponent } from 'vue';
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';

import { useLogout } from '@/composables/useLogout';
import { useHeadlineStore } from '@/stores/headline';
import { useProfileStore } from '@/stores/profile';
import { useRedirectStore } from '@/stores/redirect';
import { useSplitViewStore } from '@/stores/splitview';
import { useTokenStore } from '@/stores/token';
import { navigationItems } from '@/router/navigation';
import { routerPaths } from '@/router/router-path';

const LOGGEDOUT_ROUTES = ['login', 'entralogin', '403', '404'];
const ALWAYS_ALLOWED_ROUTES = ['contactpersons']; // temorarily enabled as these pages might have fuzzy logic for now

const paramToString = (v: unknown): string =>
  typeof v === 'string' ? v : Array.isArray(v) ? (v[0] ?? '') : '';

/**
 * To dynamic route guard
 * @param to
 * @param from
 * @param next
 * @returns
 */
const dynamicRouteGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const splitViewStore = useSplitViewStore();
  let isSplitView = splitViewStore.isSplitView;

  // let { subdomain, id, mode, subcomponent, subid } = to.params;
  const { chunk1: c1, chunk2: c2, chunk3: c3, chunk4: c4, chunk5: c5 } = to.params;
  const all = [c1, c2, c3, c4, c5]
    .filter((c) => c !== undefined)
    .join('/')
    .split('/');
  const chunk1: string = all[0] ?? '';
  const chunk2: string = all[1] ?? '';
  const chunk3: string = all[2] ?? '';
  const chunk4: string = all[3] ?? '';
  const chunk5: string = all[4] ?? '';
  let mainComponent: string;
  let mainComponentID;
  let mainComponentAction;
  let subComponent: string | undefined;
  let subComponentID;
  let subComponentAction;
  let componentNameComputed;
  let subComponentIdentifier;
  let mainSlotComponentNameComputed;
  let splitSlotComponentNameComputed;
  let componentPath;
  let mainComponentPath;
  let splitComponentPath;
  let subsplitComponentPath;

  const isAction = (str: string) => ['new', 'neu', 'edit', 'bearbeiten', 'many'].includes(str);

  const folder = () => (mainComponent ? `${mainComponent.toLowerCase()}/` : '/');
  const subfolder = () => (subComponent ? `${subComponent.toLowerCase()}/` : '');
  const mainPath = () => `../views/${folder()}`;
  const subPath = () => `../views/${folder()}${subfolder()}`;
  const emptyPath = `../views/empty/Empty.vue`;

  // `contracts/123/subcontract/345/edit`
  if (!!chunk5) {
    subComponentAction = isAction(chunk5) ? chunk5 : '';
    subComponentIdentifier = !isAction(chunk5) ? chunk5 : '';
    subComponentID = chunk4;
    subComponent = chunk3;
    mainComponentID = chunk2;
    mainComponent = chunk1;

    componentNameComputed = `${capitalizeFirstLetter(subComponent)}Detail`;
    mainSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}`;
    splitSlotComponentNameComputed = `${capitalizeFirstLetter(subComponent)}Detail`;

    componentPath = `${subPath()}${componentNameComputed}.vue`;
    mainComponentPath = `${mainPath()}${mainSlotComponentNameComputed}.vue`;
    splitComponentPath = `${mainPath()}${mainSlotComponentNameComputed}Detail.vue`;
    subsplitComponentPath = `${mainPath()}${componentNameComputed}.vue`;

    // `contracts/123/subcontract/new`
    // `contracts/123/subcontract/345`
  } else if (!!chunk4) {
    // `contracts/123/subcontract/new`
    if (isAction(chunk4)) {
      subComponentAction = chunk4;
      subComponent = chunk3;
      mainComponentID = chunk2;
      mainComponent = chunk1;

      componentNameComputed = `${capitalizeFirstLetter(subComponent)}Detail`;
      mainSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}`;
      splitSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}Detail`;

      componentPath = `${subPath()}${componentNameComputed}.vue`;
      mainComponentPath = `${mainPath()}${mainSlotComponentNameComputed}.vue`;
      splitComponentPath = `${mainPath()}${splitSlotComponentNameComputed}.vue`;
      subsplitComponentPath = `${mainPath()}${componentNameComputed}.vue`;
      // splitComponentPath = `${emptyPath}`;

      // `contracts/123/subcontract/345`
    } else {
      subComponentID = chunk4;
      subComponent = chunk3;
      mainComponentID = chunk2;
      mainComponent = chunk1;

      componentNameComputed = `${capitalizeFirstLetter(subComponent)}Detail`;
      mainSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}`;
      splitSlotComponentNameComputed = `${capitalizeFirstLetter(subComponent)}Detail`;

      componentPath = `${subPath()}${componentNameComputed}.vue`;
      mainComponentPath = `${mainPath()}${mainSlotComponentNameComputed}.vue`;
      splitComponentPath = `${mainPath()}${mainSlotComponentNameComputed}Detail.vue`;
      subsplitComponentPath = `${mainPath()}${componentNameComputed}.vue`;
    }

    // `contracts/123/edit`
    // `contracts/123/subcontract`
  } else if (!!chunk3) {
    // `contracts/123/edit`
    if (isAction(chunk3)) {
      mainComponentAction = chunk3;
      mainComponentID = chunk2;
      mainComponent = chunk1;

      const nameByAction = chunk3 === 'many' ? 'Many' : 'Detail';

      componentNameComputed = `${capitalizeFirstLetter(mainComponent)}${nameByAction}`;
      mainSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}`;
      splitSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}${nameByAction}`;

      componentPath = `${mainPath()}${componentNameComputed}.vue`;
      mainComponentPath = `${mainPath()}${mainSlotComponentNameComputed}.vue`;
      splitComponentPath = `${mainPath()}${splitSlotComponentNameComputed}.vue`;
      subsplitComponentPath = `${mainPath()}${componentNameComputed}.vue`;

      // `contracts/123/subcontract`
    } else {
      subComponent = chunk3;
      mainComponentID = chunk2;
      mainComponent = chunk1;

      componentNameComputed = `${capitalizeFirstLetter(subComponent)}`;
      mainSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}`;
      splitSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}Detail`;

      componentPath = `${mainPath()}${componentNameComputed}.vue`;
      mainComponentPath = `${mainPath()}${mainSlotComponentNameComputed}.vue`;
      splitComponentPath = `${mainPath()}${splitSlotComponentNameComputed}.vue`;
      subsplitComponentPath = `${mainPath()}${componentNameComputed}.vue`;
    }

    // `contracts/new`
    // `contracts/123`
  } else if (!!chunk2) {
    // `contracts/new`
    if (isAction(chunk2)) {
      mainComponentAction = chunk2;
      mainComponent = chunk1;

      componentNameComputed = `${capitalizeFirstLetter(mainComponent)}Detail`;
      mainSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}`;
      splitSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}Detail`;

      componentPath = `${mainPath()}${componentNameComputed}.vue`;
      mainComponentPath = `${mainPath()}${mainSlotComponentNameComputed}.vue`;
      splitComponentPath = `${mainPath()}${splitSlotComponentNameComputed}.vue`;
      subsplitComponentPath = `${mainPath()}${componentNameComputed}.vue`;
    } else if (chunk2?.startsWith('batch')) {
      // `contracts/batch` or `contracts/batch-classify` or `contracts/batch-somethingelse`
      mainComponent = chunk1;

      const batchSuffix = chunk2.slice(5);
      const batchAction = batchSuffix ? capitalizeFirstLetter(batchSuffix.replace(/^-/, '')) : '';

      componentNameComputed = `${capitalizeFirstLetter(mainComponent)}Batch${batchAction}`;
      mainSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}`;
      splitSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}Batch${batchAction}`;

      componentPath = `${mainPath()}${componentNameComputed}.vue`;
      mainComponentPath = `${mainPath()}${mainSlotComponentNameComputed}.vue`;
      splitComponentPath = `${mainPath()}${splitSlotComponentNameComputed}.vue`;
      subsplitComponentPath = `${mainPath()}${componentNameComputed}.vue`;

      isSplitView = false; // Batch-Seite braucht vermutlich keinen SplitView
    } else {
      mainComponentID = chunk2;
      mainComponent = chunk1;

      componentNameComputed = `${capitalizeFirstLetter(mainComponent)}Detail`;
      mainSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}`;
      splitSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}Detail`;

      componentPath = `${mainPath()}${componentNameComputed}.vue`;
      mainComponentPath = `${mainPath()}${mainSlotComponentNameComputed}.vue`;
      splitComponentPath = `${mainPath()}${splitSlotComponentNameComputed}.vue`;
      subsplitComponentPath = `${mainPath()}${componentNameComputed}.vue`;
    }

    // `contracts`
  } else {
    mainComponent = chunk1;

    componentNameComputed = `${capitalizeFirstLetter(mainComponent)}`;
    mainSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}`;
    splitSlotComponentNameComputed = `${capitalizeFirstLetter(mainComponent)}Detail`;

    componentPath = `${mainPath()}${componentNameComputed}.vue`;
    mainComponentPath = `${mainPath()}${mainSlotComponentNameComputed}.vue`;
    splitComponentPath = `${mainPath()}${splitSlotComponentNameComputed}.vue`;
    subsplitComponentPath = `${mainPath()}${componentNameComputed}.vue`;
    isSplitView = false; // no detail availableMemory, no splitview
  }

  function cleanObject<T extends Record<string, any>>(obj: T): T {
    // List of keys to remove
    const keysToRemove: string[] = ['chunk1', 'chunk2', 'chunk3', 'chunk4', 'chunk5'];

    // Loop through object keys and delete unwanted properties
    for (const key in obj) {
      if (
        obj[key] === '' ||
        obj[key] === null ||
        obj[key] === undefined ||
        keysToRemove.includes(key)
      ) {
        delete obj[key];
      }
    }
    return obj;
  }

  const idParam = subComponentID || mainComponentID || '';
  const modeParam = subComponentAction || mainComponentAction || '';
  const component = subComponent || mainComponent;

  to.name = `DynamicRoute-${componentNameComputed}${subComponentAction || mainComponentAction ? capitalizeFirstLetter((subComponentAction || mainComponentAction) + '') : ''}`;
  const rawParams = {
    ...to.params,
    mainComponentID: mainComponentID || '',
    subComponentID: subComponentID || '',
    id: idParam || '',
    mainComponentMode: mainComponentAction || '',
    subComponentMode: subComponentAction || '',
    mode: modeParam || '',
    component: component || '',
    mainComponent: mainComponent || '',
    subComponent: subComponent || '',
    isSplitView: isSplitView,
    subComponentIdentifier: subComponentIdentifier,
  };

  to.params = cleanObject({
    ...rawParams,
    subComponentIdentifier: rawParams.subComponentIdentifier || '',
  });

  type VueComponentModule = { default: DefineComponent };
  const viewsContext = import.meta.glob('../views/**/*.vue');

  try {
    const module = viewsContext[componentPath]
      ? ((await viewsContext[componentPath]?.()) as VueComponentModule)
      : ((await viewsContext[emptyPath]?.()) as VueComponentModule);
    const mainModule = viewsContext[mainComponentPath]
      ? ((await viewsContext[mainComponentPath]?.()) as VueComponentModule)
      : ((await viewsContext[emptyPath]?.()) as VueComponentModule);
    const splitModule = viewsContext[splitComponentPath]
      ? ((await viewsContext[splitComponentPath]?.()) as VueComponentModule)
      : ((await viewsContext[emptyPath]?.()) as VueComponentModule);
    const subsplitModule = viewsContext[subsplitComponentPath]
      ? ((await viewsContext[subsplitComponentPath]?.()) as VueComponentModule)
      : ((await viewsContext[emptyPath]?.()) as VueComponentModule);
    if (to.matched[0] && to.matched[0].components) {
      to.matched[0].components.default = module.default;

      to.matched[0].components = {
        default: module.default,
        main: mainModule.default, // Assignment for the 'list' slot
        split: splitModule.default, // Assignment for the 'detail' slot
        subsplit: subsplitModule.default,
      };

      if (subComponentAction && subComponentAction !== '') {
        to.meta.subComponentAction = subComponentAction;
        to.meta.hasSubcomponentAction = true;
      } else {
        to.meta.hasSubcomponentAction = false;
      }

      if (idParam) {
        to.params.id = idParam;
      }
      if (modeParam) {
        to.params.mode = modeParam;
      }
      next();
    } else {
      console.error('[sey-router] Matched route not found or components property is undefined.');
      next({ path: `${routerPaths[404]}` });
    }
  } catch (error) {
    console.error('[sey-router] Error loading component:', error);
    next({ path: `${routerPaths[404]}` });
  }
};

export async function globalNavigationGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  const tokenStore = useTokenStore();
  const headlineStore = useHeadlineStore();
  const redirectStore = useRedirectStore();
  const { redirectUrl } = redirectStore;
  const profileStore = useProfileStore();
  const { logout } = useLogout();
  headlineStore.$reset(); // resets headlines
  tokenStore.updateTokenFromCookies();

  // forces to stay on entralogin
  if (to.name === 'entralogin') {
    return next();
  }

  if (tokenStore.accessToken) {
    const routeSegment = paramToString(to.params.chunk1);
    const matchingNavItem = navigationItems.find((navItem) => navItem.route === `/${routeSegment}`);
    const requiresSeyboldUser = matchingNavItem ? !matchingNavItem.isCustomerAllowed : false;
    const isAlwaysAllowedRoute =
      [...LOGGEDOUT_ROUTES, ...ALWAYS_ALLOWED_ROUTES].includes(to.name as string) ||
      [...LOGGEDOUT_ROUTES, ...ALWAYS_ALLOWED_ROUTES].includes(to.params?.chunk1 as string);
    const isSeboldUser = !!profileStore.userProfile?.isSeyboldUser;

    const isAllowed = isAlwaysAllowedRoute || isSeboldUser || !requiresSeyboldUser;

    if (!isAllowed) {
      // hasLogging('sey-router') &&
      console.warn('[sey-router] Zugriff verweigert: Nur für Seybold-User erlaubt.', to.path);

      return next({ name: '403' });
    }

    if (redirectUrl) {
      if (to.path === new URL(redirectUrl, window.location.origin).pathname) {
        // clear redirect url – when redirect url is met in reality
        redirectStore.clear();
      } else {
        // redirect if not yet happened
        redirectStore.clear();
        return next(redirectUrl);
      }
    }

    return to.meta.dynamicRoute ? dynamicRouteGuard(to, from, next) : next();
  }

  if (LOGGEDOUT_ROUTES.includes(to.name as string)) {
    // hasLogging('sey-router') &&
    console.log('[sey-router] This route does not require authentication:', to.name);
    return to.meta.dynamicRoute ? dynamicRouteGuard(to, from, next) : next();
  }

  // hasLogging('sey-router') &&
  console.log('[sey-router] No token found, redirecting to login.', to.path);
  // const nextPage = !to.path.includes('entralogin') ? to.fullPath : null;
  logout({ redirect: true });
  return next(false); // Cancel navigation, logout will handle redirect
}
